import * as Sentry from "@sentry/react";

/**
 * Browser page-translators (Chrome's "Translate this page", translation
 * extensions) wrap or replace text nodes behind React's back. When React later
 * unmounts that subtree, `parent.removeChild(node)` / `parent.insertBefore(node, ref)`
 * throw `NotFoundError` and take the nearest error boundary down with them
 * (#3757, facebook/react#11538). These guards make both calls no-op on a
 * desynced node instead of throwing.
 *
 * `translate="no"` on <html> (index.html) suppresses the auto-translate
 * prompt; this guard covers manual force-translation and extensions that
 * ignore the attribute.
 */

// Lives on Node.prototype (not module scope) so it survives even an HMR
// re-evaluation of THIS module — module state would reset and let a second
// install stack a wrapper around the already-guarded methods.
const GUARD_MARKER = Symbol.for("dallali.domDesyncGuard");
type MarkablePrototype = Node & Record<symbol, boolean | undefined>;

let reportedToSentry = false;

/** Test-only: allow suites to exercise the once-per-session Sentry gate. */
export function _resetSentryReportForTest(): void {
  reportedToSentry = false;
}

// console.warn fires on EVERY suppressed desync (cheap, aids local debugging);
// the Sentry report is once per session so translator-heavy users don't flood.
function warnAndReportToSentryOnce(op: string): void {
  console.warn(
    `[dom-desync-guard] ${op} on a node whose parent changed underneath the app — likely a page translator; call suppressed (#3757)`,
  );
  if (reportedToSentry) return;
  reportedToSentry = true;
  try {
    Sentry.captureMessage("dom-desync-guard suppressed a translator-induced DOM desync", {
      level: "warning",
      tags: { guard_op: op },
    });
  } catch {
    // Sentry unavailable — the console warning above is the fallback.
  }
}

export function installDomDesyncGuard(): void {
  if (typeof Node === "undefined" || !Node.prototype) return;
  const proto = Node.prototype as MarkablePrototype;
  if (proto[GUARD_MARKER]) return;
  proto[GUARD_MARKER] = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChildGuarded<T extends Node>(
    this: Node,
    child: T,
  ): T {
    // Covers parentNode === null too: extensions that DELETE nodes outright
    // (ad-blocker cosmetic filtering) desync just like reparenting translators,
    // and native would throw the same NotFoundError.
    if (child.parentNode !== this) {
      warnAndReportToSentryOnce("removeChild");
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBeforeGuarded<T extends Node>(
    this: Node,
    newNode: T,
    reference: Node | null,
  ): T {
    if (reference && reference.parentNode !== this) {
      warnAndReportToSentryOnce("insertBefore");
      // Returned un-inserted (parentNode stays null) — fine for React, which
      // uses the return only as a chainable reference, never to verify placement.
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, reference) as T;
  };
}
